import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Popover } from "antd";
import {
  LayoutDashboard,
  Users,
  Building2,
  ClipboardList,
  FileText,
  User,
  LogOut,
  Upload,
  Menu as MenuIcon,
  X,
  BookOpen,
  Shield,
  ShieldCheck,
  Library,
  Clock,
  ChevronRight,
  ChevronDown,
  BarChart3,
  CheckSquare,
  Activity,
  UserCog,
  Calendar,
  TrendingUp,
  MapPin,
  School,
  Trophy,
  Heart,
  Image,
  KeyRound,
  Flame,
  IdCard,
  Cake,
  CalendarCheck,
  CalendarDays,
  AlertTriangle,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useTenant } from "../../hooks/useTenant";
import { cn } from "../../lib/utils";
import { canManageAvaliacoes, checkPermission } from "../../lib/permissions";
import { useMonitorAvaliacoesPermission } from "../../hooks/useMonitorAvaliacoesPermission";
import { ROLES, isGestor } from "../../constants/roles";
import { CRITICAL_SCREEN_PERMISSION_MAP } from "../../constants/permissionCatalog";
import { getFirstAccessibleAppPath } from "../../lib/defaultHomeRoute";
import ThemeToggle from "../ui/ThemeToggle";

// Configuração de subitems ocultos no menu
// TODO: Reativar items quando houver dados suficientes
// Veja DOCUMENTACAO_TABS_OCULTAS.md para mais informações
const SUBITEMS_OCULTOS = {
  "/dashboard": ["frequencia", "proficiencia"], // Oculto temporariamente no Dashboard
  "/operacoes": [], // Frequência e Avaliações estão visíveis em Operações
  "/relatorios": ["proficiencia"], // Oculto temporariamente em Relatórios
};

// Pedido da gestão-geral do NTME (10/2026); no Educa Paço continuam visíveis.
export const SUBITEMS_OCULTOS_NTME = {
  "/relatorios": ["alunos-escola", "modalidade", "cruzamento-nucleos"],
};

export const RELATORIOS_PERMITIDOS_MONITOR = ["frequencia-turma-dias", "frequencia-aluno"];

const MENU_ITEM_REQUIRED_ANY_PERMISSIONS = {
  "/dashboard": CRITICAL_SCREEN_PERMISSION_MAP["/dashboard"],
  "/gestao": CRITICAL_SCREEN_PERMISSION_MAP["/gestao"],
  "/nucleos-atividades": CRITICAL_SCREEN_PERMISSION_MAP["/nucleos-atividades"],
  "/operacoes": CRITICAL_SCREEN_PERMISSION_MAP["/operacoes"],
  "/relatorios": CRITICAL_SCREEN_PERMISSION_MAP["/relatorios"],
  "/planejamentos": CRITICAL_SCREEN_PERMISSION_MAP["/planejamentos"],
  "/galeria": CRITICAL_SCREEN_PERMISSION_MAP["/galeria"],
};

const SUBITEM_REQUIRED_PERMISSION = {
  alunos: "alunos.aluno.visualizar",
  monitores: "monitores.monitor.visualizar",
  nucleos: "nucleos.nucleo.visualizar",
  atividades: "atividades.atividade.visualizar",
  matriculas: "matriculas.matricula.visualizar",
  "gerenciamento-matriculas": "matriculas.matricula.editar",
  frequencia: "frequencia.lancamento.visualizar",
  avaliacoes: "avaliacoes.avaliacao.visualizar",
  planejamentos: "planejamentos.planejamento.visualizar",
  galeria: "galeria.midia.visualizar",
  ocorrencias: "ocorrencias.ocorrencia.visualizar",
  auditoria: "auditoria.log.visualizar",
};

const AppLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(new Set());
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const { user, logout, effectivePermissions, granularPermissionsEnabled, hasEffectivePermission } = useAuth();
  const { isNTME } = useTenant();

  const homePath = useMemo(
    () => getFirstAccessibleAppPath({ granularPermissionsEnabled, hasEffectivePermission }),
    [granularPermissionsEnabled, effectivePermissions, hasEffectivePermission]
  );

  const logoSource = isNTME ? "/assets/logo-ntme.png" : "/assets/logo-educapaco.png";
  const logoAlt = isNTME ? "NTME" : "EducaPaço";

  // Reset avatar error quando user mudar
  useEffect(() => {
    setAvatarError(false);
  }, [user?.foto_url]);
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission: monitorHasAvaliacoesPermission } = useMonitorAvaliacoesPermission();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Menu de navegação baseado nas páginas principais
  const menuItems = [
    {
      key: "/dashboard",
      icon: LayoutDashboard,
      label: "Quadro de Resumo",
      roles: ["ADMIN", "ADMINISTRATIVO", "GESTOR", "SECRETARIA", "TECNICO_GERAL", "TECNICO"],
      subItems: [
        { key: "capacidade", label: "Capacidade", icon: BarChart3 },
        { key: "frequencia", label: "Frequência", icon: CheckSquare },
        { key: "proficiencia", label: "Proficiência", icon: BookOpen },
        { key: "modalidades", label: "Atividades", icon: Activity },
        { key: "escolas", label: "Escolas", icon: School },
      ],
    },
    {
      key: "/gestao",
      icon: Building2,
      label: "Gestão",
      roles: ["ADMIN", "ADMINISTRATIVO", "GESTOR", "SECRETARIA", "TECNICO_GERAL", "TECNICO"],
      subItems: [
        { key: "usuarios", label: "Usuários", icon: Shield, roles: ["ADMIN", "ADMINISTRATIVO", "TECNICO_GERAL", "TECNICO"], isExternal: true, externalPath: "/usuarios" },
        { key: "escolas", label: "Unidades Escolares", icon: Building2, roles: ["ADMIN", "TECNICO_GERAL", "TECNICO"], isExternal: true, externalPath: "/escolas" },
        { key: "permissoes", label: "Permissões Granulares", icon: KeyRound, roles: ["TECNICO"], isExternal: true, externalPath: "/permissoes" },
        { key: "alunos", label: "Alunos", icon: Users },
        { key: "monitores", label: "Monitores", icon: UserCog, roles: ["ADMIN", "ADMINISTRATIVO", "GESTOR", "SECRETARIA", "TECNICO_GERAL", "TECNICO"] },
        { key: "anosLetivos", label: "Anos Letivos", icon: Calendar, roles: ["ADMINISTRATIVO", "TECNICO_GERAL", "TECNICO"] },
      ],
    },
    {
      key: "/nucleos-atividades",
      icon: Library,
      label: "Núcleos e Atividades",
      roles: ["ADMIN", "ADMINISTRATIVO", "GESTOR", "SECRETARIA", "TECNICO_GERAL", "TECNICO"],
      subItems: [
        { key: "nucleos", label: "Núcleos", icon: Building2 },
        { key: "atividades", label: "Atividades", icon: BookOpen, roles: ["ADMIN", "ADMINISTRATIVO", "TECNICO_GERAL", "TECNICO"] },
      ],
    },
    {
      key: "/operacoes",
      icon: ClipboardList,
      label: "Operações",
      roles: ["ADMIN", "ADMINISTRATIVO", "GESTOR", "SECRETARIA", "COORDENADOR", "MONITOR", "TECNICO_GERAL", "TECNICO"],
      subItems: [
        { key: "matriculas", label: "Matrículas", icon: BookOpen, isExternal: true, externalPath: "/matriculas", roles: ["ADMIN", "ADMINISTRATIVO", "GESTOR", "SECRETARIA", "TECNICO_GERAL", "TECNICO"] },
        { key: "gerenciamento-matriculas", label: "Gerenciamento de Matrículas", icon: BookOpen, isExternal: true, externalPath: "/gerenciamento-matriculas", roles: ["ADMIN", "ADMINISTRATIVO", "GESTOR", "SECRETARIA", "TECNICO_GERAL", "TECNICO"] },
        { key: "frequencia", label: "Frequência", icon: CheckSquare },
        { key: "avaliacoes", label: "Avaliações", icon: ClipboardList },
        { key: "historico-aluno", label: "Histórico do Aluno", icon: Clock, isExternal: true, externalPath: "/historico-aluno", roles: ["ADMIN", "ADMINISTRATIVO", "GESTOR", "SECRETARIA", "COORDENADOR", "MONITOR", "TECNICO_GERAL", "TECNICO"] },
        { key: "ocorrencias", label: "Ocorrências", icon: AlertTriangle, isExternal: true, externalPath: "/ocorrencias", roles: ["ADMIN", "ADMINISTRATIVO", "GESTOR", "SECRETARIA", "COORDENADOR", "MONITOR", "TECNICO_GERAL", "TECNICO"] },
      ],
    },
    {
      key: "/relatorios",
      icon: FileText,
      label: "Relatórios",
      roles: ["ADMIN", "ADMINISTRATIVO", "GESTOR", "SECRETARIA", "MONITOR", "TECNICO_GERAL", "TECNICO"],
      subItems: [
        { key: "alunos-turma", label: "Alunos por Turma", icon: Users },
        { key: "presencas-alunos", label: "Presenças dos Alunos", icon: CheckSquare },
        { key: "frequencia", label: "Frequência por Período", icon: Calendar },
        { key: "frequencia-turma-dias", label: "Frequência por Turma (dias)", icon: CalendarCheck },
        { key: "frequencia-aluno", label: "Frequência por Aluno", icon: CalendarDays },
        { key: "matriculas", label: "Matrículas", icon: Users, roles: ["ADMIN", "ADMINISTRATIVO", "GESTOR", "SECRETARIA", "TECNICO_GERAL", "TECNICO"] },
        { key: "alunos-escola", label: "Alunos por Escola", icon: School },
        { key: "capacidade", label: "Capacidade e Vagas", icon: BarChart3 },
        { key: "neurodivergentes", label: "Inclusão", icon: Heart },
        { key: "modalidade", label: "Modalidade Esportiva", icon: Trophy },
        { key: "cruzamento-nucleos", label: "Pedagógico x Esportivo", icon: TrendingUp },
        { key: "alunos-ativos", label: "Alunos Ativos", icon: Flame },
        { key: "cpfs-qualidade", label: "CPFs / certidões", icon: IdCard, roles: ["ADMIN", "ADMINISTRATIVO", "GESTOR", "SECRETARIA"] },
        { key: "datas-nascimento", label: "Datas de nascimento", icon: Cake, roles: ["ADMIN", "ADMINISTRATIVO", "GESTOR", "SECRETARIA"] },
        { key: "auditoria", label: "Auditoria", icon: ShieldCheck, roles: ["ADMIN", "ADMINISTRATIVO", "TECNICO_GERAL", "TECNICO"] },
      ],
    },
    {
      key: "/planejamentos",
      icon: Upload,
      label: "Planejamentos",
      roles: ["ADMIN", "ADMINISTRATIVO", "GESTOR", "SECRETARIA", "COORDENADOR", "MONITOR", "TECNICO_GERAL", "TECNICO"],
    },
    {
      key: "/galeria",
      icon: Image,
      label: "Galeria",
      roles: ["ADMIN", "ADMINISTRATIVO", "GESTOR", "SECRETARIA", "MONITOR", "TECNICO_GERAL", "TECNICO"],
    },
  ];

  // Expandir automaticamente o menu quando estiver na página correspondente
  useEffect(() => {
    const currentPath = location.pathname;
    const currentItem = menuItems.find(item => item.key === currentPath);

    // Verificar se está em uma rota externa de um subitem (como /usuarios dentro de /gestao ou /matriculas dentro de /operacoes)
    if (currentPath === "/usuarios" || currentPath === "/escolas" || currentPath === "/permissoes") {
      const gestaoItem = menuItems.find(item => item.key === "/gestao");
      if (gestaoItem) {
        setExpandedMenus(prev => {
          if (!prev.has("/gestao")) {
            return new Set([...prev, "/gestao"]);
          }
          return prev;
        });
      }
    }

    if (currentPath === "/matriculas" || currentPath === "/historico-aluno" || currentPath === "/ocorrencias") {
      const operacoesItem = menuItems.find(item => item.key === "/operacoes");
      if (operacoesItem) {
        setExpandedMenus(prev => {
          if (!prev.has("/operacoes")) {
            return new Set([...prev, "/operacoes"]);
          }
          return prev;
        });
      }
    }

    if (currentItem && currentItem.subItems) {
      setExpandedMenus(prev => {
        if (!prev.has(currentPath)) {
          return new Set([...prev, currentPath]);
        }
        return prev;
      });
    }
  }, [location.pathname, location.search]);

  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(menuKey)) {
        newSet.delete(menuKey);
      } else {
        newSet.add(menuKey);
      }
      return newSet;
    });
  };

  const handleSubItemClick = (parentKey, subItemKey, subItem) => {
    // Se for um item externo, navegar para o caminho externo
    if (subItem?.isExternal && subItem?.externalPath) {
      navigate(subItem.externalPath);
    } else {
      navigate(`${parentKey}?tab=${subItemKey}`);
    }
    if (window.innerWidth < 1024) {
      setMobileMenuOpen(false);
    }
  };

  const handleMainItemClick = (item) => {
    if (item.subItems && item.subItems.length > 0) {
      // Se tem subitems, navega para a primeira aba
      const firstSubItem = item.subItems[0];
      navigate(`${item.key}?tab=${firstSubItem.key}`);
    } else {
      // Se não tem subitems, navega normalmente
      navigate(item.key);
    }
    if (window.innerWidth < 1024) {
      setMobileMenuOpen(false);
    }
  };

  // Verifica permissão de avaliações considerando vinculação a atividades pedagógicas para MONITOR
  const temPermissaoAvaliacoes = canManageAvaliacoes(
    user?.role,
    user?.role === "MONITOR" ? monitorHasAvaliacoesPermission : null,
    effectivePermissions
  );
  const temPermissaoAlunos = granularPermissionsEnabled
    ? hasEffectivePermission("alunos.aluno.visualizar")
    : checkPermission(user?.role, "alunos.aluno.visualizar", effectivePermissions);
  const temPermissaoMonitores = granularPermissionsEnabled
    ? hasEffectivePermission("monitores.monitor.visualizar")
    : checkPermission(user?.role, "monitores.monitor.visualizar", effectivePermissions);

  // Filtra itens do menu baseado no role do usuário e nos subitems ocultos
  const userRole = user?.role?.toUpperCase();
  const filteredMenuItems = menuItems
    .filter(item => {
      const roleAllowed = !item.roles || item.roles.some(role => role.toUpperCase() === userRole);
      if (!roleAllowed) return false;
      if (!granularPermissionsEnabled) return true;

      const anyPermissions = MENU_ITEM_REQUIRED_ANY_PERMISSIONS[item.key];
      if (!Array.isArray(anyPermissions) || !anyPermissions.length) return true;

      return anyPermissions.some(permissionCode => hasEffectivePermission(permissionCode));
    })
    .map(item => {
      if (item.subItems) {
        const subItemsOcultos = [
          ...(SUBITEMS_OCULTOS[item.key] || []),
          ...(isNTME ? SUBITEMS_OCULTOS_NTME[item.key] || [] : []),
        ];
        return {
          ...item,
          subItems: item.subItems.map(subItem => {
            // Verifica roles do subitem
            if (subItem.roles && !subItem.roles.some(role => role.toUpperCase() === userRole)) {
              return { ...subItem, disabled: true };
            }
            // Verifica se está na lista de ocultos
            if (subItemsOcultos.includes(subItem.key)) {
              return { ...subItem, disabled: true };
            }
            // Verifica permissão específica para avaliações - mostra mas desabilita se não tiver permissão
            if (subItem.key === "avaliacoes") {
              return { ...subItem, disabled: !temPermissaoAvaliacoes };
            }
            if (granularPermissionsEnabled) {
              // Regras granulares específicas da Gestão.
              if (subItem.key === "alunos" && !temPermissaoAlunos) {
                return { ...subItem, hiddenByGranularPermission: true };
              }
              if (subItem.key === "monitores" && !temPermissaoMonitores) {
                return { ...subItem, hiddenByGranularPermission: true };
              }

              // Regra geral por subitem.
              const requiredPermission = SUBITEM_REQUIRED_PERMISSION[subItem.key];
              if (requiredPermission && !hasEffectivePermission(requiredPermission)) {
                return { ...subItem, hiddenByGranularPermission: true };
              }
            }
            return subItem;
          }).filter(subItem => {
            if (subItem.hiddenByGranularPermission) {
              return false;
            }
            if (item.key === "/relatorios" && userRole === ROLES.MONITOR && !RELATORIOS_PERMITIDOS_MONITOR.includes(subItem.key)) {
              return false;
            }
            // Remove itens ocultos completamente, mas mantém avaliações mesmo desabilitado
            if (subItemsOcultos.includes(subItem.key)) {
              return false;
            }
            // Ocultar "atividades" para gestores
            if (subItem.key === "atividades" && isGestor(user?.role)) {
              return false;
            }
            // Para avaliações, sempre mostra (mesmo que desabilitado)
            if (subItem.key === "avaliacoes") {
              return true;
            }
            // Para outros itens com roles, remove se não tiver acesso (case insensitive)
            if (subItem.roles && !subItem.roles.some(role => role.toUpperCase() === userRole)) {
              return false;
            }
            return true;
          }),
        };
      }
      return item;
    })
    .filter(item => !item.subItems || item.subItems.length > 0);

  const selectedKeys = [location.pathname];
  const searchParams = new URLSearchParams(location.search);
  const activeTab = searchParams.get("tab");

  // Verificar se está em uma rota que corresponde a um subitem externo
  const getActiveSubItem = () => {
    if (location.pathname === "/usuarios") {
      return "usuarios";
    }
    if (location.pathname === "/escolas") {
      return "escolas";
    }
    if (location.pathname === "/permissoes") {
      return "permissoes";
    }
    if (location.pathname === "/matriculas") {
      return "matriculas";
    }
    if (location.pathname === "/historico-aluno") {
      return "historico-aluno";
    }
    if (location.pathname === "/ocorrencias") {
      return "ocorrencias";
    }
    return activeTab;
  };

  const currentActiveTab = getActiveSubItem();

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar Desktop */}
      <aside
        className={cn(
          "hidden lg:flex flex-col fixed left-0 top-0 bottom-0 z-50 bg-sidebar border-r border-sidebar-border transition-all duration-300",
          collapsed ? "w-20" : "w-64"
        )}
      >
        {/* Logo */}
        <div className="h-28 flex items-center justify-center px-4 border-b border-sidebar-border relative">
          <Link to={homePath} className="flex items-center justify-center w-full h-full">
            {collapsed ? (
              <img
                src={logoSource}
                alt={logoAlt}
                className={cn("w-20 h-20 object-contain", isNTME && "py-2")}
              />
            ) : (
              <img
                src={logoSource}
                alt={logoAlt}
                className={cn("h-24 w-auto max-w-[calc(100%-32px)] object-contain mx-auto", isNTME && "py-3")}
              />
            )}
          </Link>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="absolute right-2 p-1.5 rounded-md hover:bg-sidebar-accent transition-colors lg:hidden"
          >
            <X className="w-4 h-4 text-sidebar-foreground" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2">
          <div className="space-y-1">
            {filteredMenuItems.map(item => {
              const Icon = item.icon;
              // Verificar se está ativo: diretamente na rota ou em um subitem externo
              const isActive = selectedKeys.includes(item.key) ||
                ((location.pathname === "/usuarios" || location.pathname === "/escolas") && item.key === "/gestao") ||
                ((location.pathname === "/matriculas" || location.pathname === "/historico-aluno" || location.pathname === "/ocorrencias") && item.key === "/operacoes");
              const isExpanded = expandedMenus.has(item.key);
              const hasSubItems = item.subItems && item.subItems.length > 0;

              return (
                <div key={item.key}>
                  <div className="flex items-center">
                    <button
                      onClick={() => handleMainItemClick(item)}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors relative group flex-1 text-left",
                        isActive
                          ? "bg-sidebar-accent text-sidebar-accent-foreground"
                          : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                      )}
                      title={collapsed ? item.label : undefined}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
                      {isActive && !collapsed && !hasSubItems && (
                        <motion.div
                          layoutId="activeIndicator"
                          className="absolute right-2 w-1.5 h-1.5 rounded-full bg-sidebar-primary"
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      )}
                    </button>
                    {hasSubItems && !collapsed && (
                      <button
                        onClick={() => toggleMenu(item.key)}
                        className={cn(
                          "p-2 rounded-md transition-colors",
                          isActive
                            ? "text-sidebar-accent-foreground hover:bg-sidebar-accent/80"
                            : "text-sidebar-foreground hover:bg-sidebar-accent"
                        )}
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                  {hasSubItems && !collapsed && (
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="ml-4 mt-1 space-y-1 border-l border-sidebar-border pl-2">
                            {item.subItems.map(subItem => {
                              const SubIcon = subItem.icon;
                              const isSubActive = subItem.isExternal
                                ? location.pathname === subItem.externalPath
                                : isActive && currentActiveTab === subItem.key;
                              const isDisabled = subItem.disabled === true;
                              return (
                                <button
                                  key={subItem.key}
                                  onClick={() => {
                                    if (!isDisabled) {
                                      handleSubItemClick(item.key, subItem.key, subItem);
                                    }
                                  }}
                                  disabled={isDisabled}
                                  className={cn(
                                    "flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors w-full text-left",
                                    isDisabled
                                      ? "text-sidebar-foreground/40 cursor-not-allowed opacity-50"
                                      : isSubActive
                                        ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                                        : "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                                  )}
                                  title={isDisabled && subItem.key === "avaliacoes" ? "Você precisa estar vinculado a uma atividade pedagógica ou esportiva para acessar avaliações" : undefined}
                                >
                                  <SubIcon className={cn("w-4 h-4 flex-shrink-0", isDisabled && "opacity-50")} />
                                  <span className="truncate">{subItem.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={cn(
          "lg:hidden fixed left-0 top-0 bottom-0 z-50 w-64 bg-sidebar border-r border-sidebar-border transform transition-transform duration-300",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="h-28 flex items-center justify-center px-4 border-b border-sidebar-border relative">
          <Link to={homePath} className="flex items-center justify-center w-full h-full">
            <img
              src={logoSource}
              alt={logoAlt}
              className={cn("h-50 w-auto max-w-[calc(100%-32px)] object-contain", isNTME && "py-3")}
            />
          </Link>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="absolute right-2 p-1.5 rounded-md hover:bg-sidebar-accent transition-colors"
          >
            <X className="w-5 h-5 text-sidebar-foreground" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-2">
          <div className="space-y-1">
            {filteredMenuItems.map(item => {
              const Icon = item.icon;
              // Verificar se está ativo: diretamente na rota ou em um subitem externo
              const isActive = selectedKeys.includes(item.key) ||
                ((location.pathname === "/usuarios" || location.pathname === "/escolas") && item.key === "/gestao") ||
                ((location.pathname === "/matriculas" || location.pathname === "/historico-aluno" || location.pathname === "/ocorrencias") && item.key === "/operacoes");
              const isExpanded = expandedMenus.has(item.key);
              const hasSubItems = item.subItems && item.subItems.length > 0;

              return (
                <div key={item.key}>
                  <div className="flex items-center">
                    <button
                      onClick={() => handleMainItemClick(item)}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors flex-1 text-left",
                        isActive
                          ? "bg-sidebar-accent text-sidebar-accent-foreground"
                          : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                      )}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{item.label}</span>
                      {isActive && !hasSubItems && <ChevronRight className="w-4 h-4 ml-auto" />}
                    </button>
                    {hasSubItems && (
                      <button
                        onClick={() => toggleMenu(item.key)}
                        className={cn(
                          "p-2 rounded-md transition-colors",
                          isActive
                            ? "text-sidebar-accent-foreground hover:bg-sidebar-accent/80"
                            : "text-sidebar-foreground hover:bg-sidebar-accent"
                        )}
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                  {hasSubItems && (
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="ml-4 mt-1 space-y-1 border-l border-sidebar-border pl-2">
                            {item.subItems.map(subItem => {
                              const SubIcon = subItem.icon;
                              const isSubActive = subItem.isExternal
                                ? location.pathname === subItem.externalPath
                                : isActive && currentActiveTab === subItem.key;
                              const isDisabled = subItem.disabled === true;
                              return (
                                <button
                                  key={subItem.key}
                                  onClick={() => {
                                    if (!isDisabled) {
                                      handleSubItemClick(item.key, subItem.key, subItem);
                                    }
                                  }}
                                  disabled={isDisabled}
                                  className={cn(
                                    "flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors w-full text-left",
                                    isDisabled
                                      ? "text-sidebar-foreground/40 cursor-not-allowed opacity-50"
                                      : isSubActive
                                        ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                                        : "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                                  )}
                                  title={isDisabled && subItem.key === "avaliacoes" ? "Você precisa estar vinculado a uma atividade pedagógica ou esportiva para acessar avaliações" : undefined}
                                >
                                  <SubIcon className={cn("w-4 h-4 flex-shrink-0", isDisabled && "opacity-50")} />
                                  <span>{subItem.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

      </aside>

      {/* Main Content */}
      <div
        className={cn(
          "flex-1 flex flex-col min-w-0",
          "lg:ml-0",
          collapsed ? "lg:ml-20" : "lg:ml-64"
        )}
      >
        {/* Top Header */}
        <header className="sticky top-0 z-30 h-16 bg-primary backdrop-blur-xl border-b border-primary/20 flex items-center justify-between px-4 lg:px-6">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-primary/80 transition-colors"
          >
            <MenuIcon className="w-5 h-5 text-primary-foreground" />
          </button>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex p-2 rounded-lg hover:bg-primary/80 transition-colors"
          >
            <MenuIcon className="w-5 h-5 text-primary-foreground" />
          </button>
          <div className="flex items-center gap-4 ml-auto">
            <ThemeToggle />
            <Popover
              content={
                <div className="min-w-[200px]">
                  <div className="px-2 py-2 border-b border-gray-200 mb-2">
                    <div className="text-sm font-medium text-gray-900">
                      {user?.nome || user?.email}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {user?.role || "Usuário"}
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <Link
                      to="/perfil"
                      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <User className="w-4 h-4" />
                      <span>Meu Perfil</span>
                    </Link>
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors mt-1"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sair</span>
                    </button>
                  </div>
                </div>
              }
              trigger="click"
              placement="bottomRight"
              open={userMenuOpen}
              onOpenChange={setUserMenuOpen}
              overlayClassName="user-menu-popover"
            >
              <div className="flex items-center gap-3 cursor-pointer">
                <div className="hidden sm:block text-sm text-primary-foreground">
                  {user?.nome || user?.email}
                </div>
                {user?.foto_url && !avatarError ? (
                  <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-primary-foreground/30 hover:border-primary-foreground/50 transition-colors">
                    <img
                      src={user.foto_url}
                      alt={user?.nome || "Avatar"}
                      className="w-full h-full object-cover"
                      onError={() => setAvatarError(true)}
                    />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center hover:bg-primary-foreground/30 transition-colors">
                    {user?.nome ? (
                      <span className="text-xs font-semibold text-primary-foreground">
                        {user.nome.charAt(0).toUpperCase()}
                      </span>
                    ) : (
                      <User className="w-4 h-4 text-primary-foreground" />
                    )}
                  </div>
                )}
              </div>
            </Popover>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="container max-w-7xl mx-auto px-4 py-6 lg:px-6 lg:py-8">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
